import { useState } from 'react';

const steps = [
  {
    phase: '1단계',
    timing: '간병 시작 전',
    icon: 'ri-hospital-line',
    title: '병원 사전확인',
    subtitle: '간병 시작 전 반드시 확인하세요',
    details: [
      { label: '간호기록지 작성', desc: '병원에 간호기록지를 작성해 줄 수 있는지 확인하세요.', important: true },
      { label: '간병인 입출입 기록', desc: '간병인의 입출입을 기록해 줄 수 있는지 확인하세요.', important: true },
      { label: '확인 방법', desc: '담당 간호사 또는 원무과에 문의하세요.' },
    ],
    warning: '보험금 지급을 위한 필수 사항입니다. 반드시 사전에 확인하세요.',
  },
  {
    phase: '2단계',
    timing: '간병 기간 중',
    icon: 'ri-edit-box-line',
    title: '간병일지 작성',
    subtitle: '간병인은 매일 간병일지를 작성합니다',
    details: [
      { label: '간병일지 양식', desc: '코리아케어러스에서 제공하는 공식 양식을 사용하세요.', important: true },
      { label: '작성 주체', desc: '간병인이 매일 직접 작성해야 합니다.' },
      { label: '작성 내용', desc: '간병 시간, 환자 상태, 수행 업무 등을 상세히 기록하세요.' },
    ],
  },
  {
    phase: '3단계',
    timing: '간병 종료 직후',
    icon: 'ri-money-dollar-circle-line',
    title: '정산 및 증빙',
    subtitle: '간병비 정산과 증빙자료를 확보하세요',
    details: [
      { label: '간병비 이체', desc: '환자 계좌에서 간병인 계좌로 직접 이체하세요. 이체 메모에 반드시 "간병비 지급"이라고 기재하세요.', important: true },
      { label: '이체확인증 캡쳐', desc: '송금 완료 후 이체확인증(송금확인증)을 반드시 캡쳐해 보관하세요.', important: true },
      { label: '회사 수수료 지급', desc: '코리아케어러스에 정해진 중개 수수료를 별도로 지급하세요.' },
    ],
    warning: '환자 이름과 간병인 이름이 계좌 정보와 일치해야 합니다.',
  },
  {
    phase: '4단계',
    timing: '정산 완료 후',
    icon: 'ri-bank-line',
    title: '보험금 청구',
    subtitle: '서류를 취합하여 보험사에 청구하세요',
    details: [
      { label: '회사에 서류 제출', desc: '간호기록지, 입퇴원확인서, 간병일지, 이체확인증을 당사에 보내주세요.', important: true },
      { label: '회사가 3종 발급', desc: '이용내역 확인서, 회원가입 사실확인서, 사업자등록증을 발급해 드립니다.' },
      { label: '보험사에 청구', desc: '취합한 모든 서류를 가지고 보험회사에 보험금 지급을 요청하세요.' },
    ],
  },
];

export default function DocumentProcess() {
  return (
    <section className="w-full py-16 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-14">
          <p className="text-xs font-semibold tracking-widest uppercase mb-2" style={{ color: '#2E7D6B' }}>INSURANCE CLAIM PROCESS</p>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900">보험청구 4단계 프로세스</h2>
          <p className="text-sm text-gray-500 mt-2 max-w-2xl mx-auto leading-relaxed">
            간병 시작 전 사전확인부터 보험금 수령까지, 놓치기 쉬운 모든 절차를 단계별로 정리했습니다
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical line on desktop, horizontal on mobile */}
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px -translate-x-1/2" style={{ backgroundColor: 'rgba(46,125,107,0.15)' }} />

          <div className="space-y-12 md:space-y-16">
            {steps.map((step, idx) => {
              const isEven = idx % 2 === 0;
              return (
                <div key={idx} className="relative">
                  {/* Timeline dot */}
                  <div className="hidden md:flex absolute left-1/2 top-8 -translate-x-1/2 z-10 items-center justify-center">
                    <div className="w-12 h-12 rounded-full border-4 border-white flex items-center justify-center" style={{ backgroundColor: '#2E7D6B' }}>
                      <span className="text-white font-black text-sm">{idx + 1}</span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className={`flex flex-col md:flex-row items-start gap-0 ${isEven ? '' : 'md:flex-row-reverse'}`}>
                    {/* Spacer */}
                    <div className="hidden md:block w-1/2" />

                    {/* Card */}
                    <div className="w-full md:w-1/2 md:px-10">
                      <div className="rounded-2xl border p-6" style={{ backgroundColor: '#fafffe', borderColor: 'rgba(46,125,107,0.12)' }}>
                        {/* Mobile step badge */}
                        <div className="flex md:hidden items-center gap-3 mb-4">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#2E7D6B' }}>
                            <span className="text-white font-black text-sm">{idx + 1}</span>
                          </div>
                          <div>
                            <span className="text-[10px] font-bold tracking-widest uppercase" style={{ color: '#2E7D6B' }}>{step.phase}</span>
                            <span className="text-xs text-gray-400 ml-2">{step.timing}</span>
                          </div>
                        </div>

                        {/* Desktop header */}
                        <div className="hidden md:flex items-center gap-2 mb-1">
                          <span className="text-[10px] font-bold tracking-widest uppercase" style={{ color: '#2E7D6B' }}>{step.phase}</span>
                          <span className="w-1 h-1 rounded-full bg-gray-300" />
                          <span className="text-xs text-gray-400">{step.timing}</span>
                        </div>

                        <div className="flex items-center gap-2.5 mb-3">
                          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(46,125,107,0.1)' }}>
                            <i className={`${step.icon} text-lg`} style={{ color: '#2E7D6B' }} />
                          </div>
                          <div>
                            <h3 className="text-base font-bold text-gray-900">{step.title}</h3>
                            <p className="text-xs text-gray-500">{step.subtitle}</p>
                          </div>
                        </div>

                        {/* Detail list */}
                        <div className="space-y-2.5">
                          {step.details.map((detail, didx) => (
                            <div key={didx} className="flex gap-3">
                              <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: detail.important ? 'rgba(46,125,107,0.12)' : 'rgba(0,0,0,0.04)' }}>
                                <i
                                  className={`${detail.important ? 'ri-error-warning-line' : 'ri-check-line'} text-xs`}
                                  style={{ color: detail.important ? '#2E7D6B' : '#9ca3af' }}
                                />
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-gray-800">{detail.label}</p>
                                <p className="text-xs text-gray-500 leading-relaxed">{detail.desc}</p>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Warning */}
                        {step.warning && (
                          <div className="mt-4 rounded-xl px-4 py-3 flex items-start gap-2" style={{ backgroundColor: 'rgba(255,193,7,0.07)', border: '1px solid rgba(255,193,7,0.2)' }}>
                            <i className="ri-alert-line text-sm mt-0.5 flex-shrink-0" style={{ color: '#b8860b' }} />
                            <p className="text-xs text-gray-600 leading-relaxed">{step.warning}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}